import React from 'react';
import { useCourseList } from '@/hooks/queries/course/useList';
import { Course } from '@/types/courses';
import { getCourseTitle } from '@/utils/common';
import { BookOpen } from 'lucide-react';
import { cn } from '@/utils/styleUtils';

interface CourseSelectProps {
  value?: string;
  onChange: (courseId: string) => void;
  label?: string;
  placeholder?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  required?: boolean;
  className?: string;
}

const extractCourses = (data: any): Course[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.data)) return data.data.data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.courses)) return data.courses;
  return [];
};

export const CourseSelect: React.FC<CourseSelectProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Kursni tanlang...',
  allowEmpty = false,
  emptyLabel = 'Barcha kurslar (Umumiy)',
  required = false,
  className,
}) => {
  const { data: coursesData, isLoading } = useCourseList();
  const courses = extractCourses(coursesData);

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label className="text-xs font-semibold text-slate-700 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full h-10 px-3 pr-8 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
        >
          {allowEmpty && <option value="">{emptyLabel}</option>}
          {!allowEmpty && <option value="" disabled>{placeholder}</option>}
          {courses.map((course) => {
            const rawTitle = getCourseTitle(course);
            const title = rawTitle || (course.format ? `${course.format} Kursi` : `Kurs ${course.id.slice(0, 8)}`);
            return (
              <option key={course.id} value={course.id}>
                {course.format ? `[${course.format}] ` : ''}{title}
              </option>
            );
          })}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
          <BookOpen className="size-4" />
        </div>
      </div>
    </div>
  );
};
